/**
 * Match Records Page
 * Dispute evidence — screenshots + metadata for every completed LIVE game
 * (Spades / 4-in-a-Row / Crazy Eights). Admin & super-admin only.
 *
 * Feb 2026: paired with the new /api/game/{id}/result-screenshot and
 * /api/admin/match-records endpoints in backend/server.py.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Trophy,
  Clock,
  Coins,
  Users as UsersIcon,
  X,
  ImageIcon,
} from 'lucide-react';

const GAME_TYPE_LABELS = {
  spades: 'Spades',
  four_in_a_row: '4 In A Row',
  crazy_eights: 'Crazy Eights',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const imgSrc = (b64) => {
  if (!b64) return null;
  return b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
};

const MatchRecordsPage = () => {
  const { api } = useAdmin();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [pendingQ, setPendingQ] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(20);
  const [selected, setSelected] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (q) params.q = q;
      const res = await api.get('/api/admin/match-records', { params });
      setRecords(res.data?.records || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to load match records';
      toast.error(msg);
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [api, q, page, perPage]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setQ(pendingQ.trim());
  };

  return (
    <div className="p-4 md:p-6" data-testid="match-records-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-400" />
            Match Records
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            LIVE game screenshots + metadata for dispute review. Practice games are not recorded.
          </p>
        </div>

        <form onSubmit={submitSearch} className="mb-4 flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={pendingQ}
              onChange={(e) => setPendingQ(e.target.value)}
              placeholder="Search by @handle, name, or user ID"
              data-testid="match-records-search-input"
              className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
            />
          </div>
          <button
            type="submit"
            data-testid="match-records-search-btn"
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg"
          >
            Search
          </button>
          {q && (
            <button
              type="button"
              onClick={() => { setPendingQ(''); setQ(''); setPage(1); }}
              className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-gray-300 text-sm rounded-lg"
            >
              Clear
            </button>
          )}
        </form>

        <div className="text-xs text-gray-500 mb-3" data-testid="match-records-summary">
          {loading ? 'Loading…' : `${total} record${total === 1 ? '' : 's'}${q ? ` for "${q}"` : ''}`}
        </div>

        <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/70 text-gray-300">
              <tr>
                <th className="text-left px-3 py-2">Game</th>
                <th className="text-left px-3 py-2">Participants</th>
                <th className="text-left px-3 py-2">Start</th>
                <th className="text-left px-3 py-2">End</th>
                <th className="text-left px-3 py-2">Result</th>
                <th className="text-left px-3 py-2">Entry</th>
                <th className="text-left px-3 py-2">Evidence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading && (
                <tr><td colSpan={7} className="text-center py-6 text-gray-500">Loading…</td></tr>
              )}
              {!loading && records.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-gray-500" data-testid="match-records-empty">No match records found.</td></tr>
              )}
              {!loading && records.map((r) => {
                const winner = (r.participants || []).find(p => p.user_id === r.winner_id);
                return (
                  <tr key={r.id || r.game_id} className="hover:bg-slate-800/40" data-testid={`match-record-row-${r.game_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-white">
                        {GAME_TYPE_LABELS[r.game_type] || r.game_type}
                      </div>
                      <div className="text-[11px] text-gray-500">{r.game_id}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-start gap-1 text-gray-300 text-xs">
                        <UsersIcon className="w-3 h-3 mt-0.5 text-gray-500" />
                        <div className="space-y-0.5">
                          {(r.participants || []).map((p, i) => (
                            <div key={i} className={p.user_id === r.winner_id ? 'text-emerald-400 font-semibold' : ''}>
                              {p.name || '—'}
                              {p.handle && (
                                <span className="text-gray-500 ml-1">
                                  {p.handle.startsWith('@') ? p.handle : `@${p.handle}`}
                                </span>
                              )}
                            </div>
                          ))}
                          {(r.participants || []).length === 0 && <span className="text-gray-500">—</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-400 text-xs">
                      <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(r.started_at)}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-400 text-xs">
                      <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(r.ended_at)}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-300 text-xs">
                      <div className="flex items-center gap-1 mb-0.5">
                        <Trophy className="w-3 h-3 text-yellow-400" />
                        <span className="text-emerald-400 font-semibold">{winner?.name || r.winner_id || '—'}</span>
                      </div>
                      <div className="text-[11px] text-gray-500">{r.result_summary || '—'}</div>
                    </td>
                    <td className="px-3 py-2 text-emerald-300 text-xs">
                      <div className="flex items-center gap-1">
                        <Coins className="w-3 h-3" /> {r.wager || 0}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {r.image_base64 ? (
                        <button
                          type="button"
                          onClick={() => setSelected(r)}
                          data-testid={`match-record-thumb-${r.game_id}`}
                          className="block h-14 w-24 rounded overflow-hidden ring-1 ring-slate-700 hover:ring-emerald-500"
                          title="Click to enlarge"
                        >
                          <img
                            src={imgSrc(r.image_base64)}
                            alt="Match result"
                            className="h-full w-full object-cover"
                          />
                        </button>
                      ) : (
                        <div className="h-14 w-24 rounded bg-slate-800 flex items-center justify-center text-gray-500">
                          <ImageIcon className="w-4 h-4" />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > perPage && (
          <div className="mt-4 flex items-center justify-between text-sm text-gray-400">
            <div>Page {page} of {totalPages}</div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 rounded flex items-center gap-1"
                data-testid="match-records-prev"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <button
                type="button"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages || loading}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 rounded flex items-center gap-1"
                data-testid="match-records-next"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Enlarged image modal */}
        {selected && (
          <div
            className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
            data-testid="match-record-enlarge-modal"
          >
            <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-3">
                <div className="text-white">
                  <div className="font-semibold flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-yellow-400" />
                    {GAME_TYPE_LABELS[selected.game_type] || selected.game_type} · {selected.result_summary || '—'}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {(selected.participants || []).map(p => p.name).filter(Boolean).join(' · ')}
                    {' — '}
                    {formatDate(selected.started_at)} → {formatDate(selected.ended_at)}
                    {' · entry '}{selected.wager || 0}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="p-2 rounded-full bg-slate-800 hover:bg-slate-700"
                  data-testid="match-record-enlarge-close"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
              <img
                src={imgSrc(selected.image_base64)}
                alt="Match result full"
                className="w-full rounded-lg ring-1 ring-slate-700"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchRecordsPage;
